import React, { useMemo } from "react";
import PropTypes from "prop-types";
import TextBlock from "../../../components/Preview/Draggable/Block/TextBlock";
import {
  resolveAiBannerLabelFontSize,
  resolveAiBannerTypography,
} from "../../../../shared/ai-recommendation-upsell-schema.js";
import { parseAiRibbonText } from "../../../../shared/ai-recommendation-copy.js";
import PopupPitchSkeleton from "../../../components/Preview/catalog-product/PopupPitchSkeleton";
import styles from "./AiRecommendationRibbon.module.css";
import { buildFontFamilyCssVar, buildFontFamilyStyle } from "../../../helper";

export default function AiRecommendationRibbon({
  section,
  popupData,
  activeStep,
  index,
  parentData,
  displayText,
  loading = false,
  isMobile = false,
}) {
  const { label, summary } = parseAiRibbonText(displayText ?? section?.text ?? "");
  const viewKey = isMobile ? "mobile" : "desktop";
  const typo = useMemo(
    () => resolveAiBannerTypography(section, viewKey),
    [section, viewKey]
  );

  const ribbonStyle = useMemo(
    () => ({
      ...buildFontFamilyStyle(typo.fontFamily),
      ...buildFontFamilyCssVar("--ai-banner-font-family", typo.fontFamily),
      "--ai-banner-font-size": typo.fontSize,
      "--ai-banner-font-weight": typo.fontWeight,
      "--ai-banner-line-height": typo.lineHeight,
      "--ai-banner-color": typo.color,
      "--ai-banner-label-size": resolveAiBannerLabelFontSize(typo.fontSize),
      "--ai-banner-text-align": typo.textAlign,
    }),
    [typo]
  );

  return (
    <div
      className={styles.ribbon}
      style={ribbonStyle}
      data-loading={loading ? "true" : undefined}
    >
      <span className={styles.iconWrap} aria-hidden>
        <svg className={styles.iconSvg} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 2.5l1.35 4.05L17.5 8l-4.15 1.45L12 13.5l-1.35-4.05L6.5 8l4.15-1.45L12 2.5z"
            fill="currentColor"
          />
          <path
            d="M18.5 13.5l.9 2.7L22 17l-2.6.9L18.5 20.5l-.9-2.6L15 17l2.6-.9.9-2.6z"
            fill="currentColor"
          />
          <path
            d="M5.5 15.5l.65 1.95L8.5 18l-1.95.65L5.5 20.5l-.65-1.95L3 18l1.95-.65.65-1.85z"
            fill="currentColor"
          />
        </svg>
      </span>
      <div className={styles.meta}>
        <span className={styles.label}>{label}</span>
        {loading ? (
          <PopupPitchSkeleton compact={isMobile} />
        ) : summary ? (
          <div className={styles.summary}>
            <TextBlock
              popupData={popupData}
              activeStep={activeStep}
              index={index}
              section={{ ...section, text: summary }}
              parentData={parentData}
              catalogFlow
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

AiRecommendationRibbon.propTypes = {
  section: PropTypes.object,
  popupData: PropTypes.object,
  activeStep: PropTypes.number,
  index: PropTypes.number,
  parentData: PropTypes.object,
  displayText: PropTypes.string,
  loading: PropTypes.bool,
  isMobile: PropTypes.bool,
};
